import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, Instagram, Linkedin, Send, MapPin, CheckCircle2, MessageCircle } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';
import { company, contactOptions } from '../mock/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: '', email: '', company: '', website: '', industry: '', budget: '', markets: '', campaign_type: '', message: '' };

export default function Contact() {
  useReveal('contact');
  useSeo({ title: 'Contact | Start a Campaign with Interscope Ads', description: 'Talk to our team. Start a performance marketing strategy around your goals, markets and KPIs.' });
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete required fields', description: 'Name, work email and message are required.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: form.company,
        website: form.website,
        industry: form.industry,
        budget: form.budget,
        markets: form.markets,
        campaign_type: form.campaign_type,
        role: form.campaign_type || 'Advertiser',
        message: form.message,
      });
      setDone(true);
      setForm(initial);
      toast({ title: 'Message sent!', description: 'Thanks \u2014 our team will reach out shortly.' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: 'Something went wrong', description: typeof detail === 'string' ? detail : 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: 'Phone', value: company.phone, href: `tel:${company.phone}` },
    { icon: MessageCircle, label: 'WhatsApp', value: company.phone, href: `https://wa.me/${company.whatsapp}` },
    { icon: Mail, label: 'Email', value: company.email, href: `mailto:${company.email}` },
    { icon: Instagram, label: 'Instagram', value: '@interscope_ads', href: company.instagram },
    { icon: Linkedin, label: 'LinkedIn', value: 'Interscope Tech Media', href: company.linkedin },
  ];

  const inputCls = 'bg-slate-100 border-slate-200 text-slate-900 placeholder:text-slate-500 h-12';

  return (
    <main className="pt-32 pb-10">
      <section className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-14">
          <span className="reveal inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">Contact Us</span>
          <h1 className="reveal font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.08] max-w-3xl mx-auto">
            Start the <span className="text-gradient">conversation</span>
          </h1>
          <p className="reveal mt-6 text-slate-600 text-lg max-w-xl mx-auto">
            Tell us about your goals, markets and KPIs. We reply within one business day.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="reveal lg:col-span-2 space-y-4">
            {contactItems.map((c) => (
              <a key={c.label} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="flex items-center gap-4 glass glass-hover rounded-2xl p-5 group">
                <div className="w-12 h-12 rounded-xl bg-[#2563eb]/12 grid place-items-center text-[#2563eb] shrink-0 group-hover:scale-110 transition-transform"><c.icon size={22} /></div>
                <div><div className="text-xs text-slate-500">{c.label}</div><div className="text-slate-900 font-medium">{c.value}</div></div>
              </a>
            ))}
            <div className="flex items-center gap-4 glass rounded-2xl p-5">
              <div className="w-12 h-12 rounded-xl bg-[#2563eb]/12 grid place-items-center text-[#2563eb] shrink-0"><MapPin size={22} /></div>
              <div><div className="text-xs text-slate-500">Working with brands in</div><div className="text-slate-900 font-medium">150+ global markets</div></div>
            </div>
          </div>

          <div className="reveal lg:col-span-3">
            {done ? (
              <div className="glass-strong rounded-3xl p-10 md:p-14 text-center h-full grid place-items-center">
                <div>
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#2563eb]/15 grid place-items-center text-[#2563eb] mb-5"><CheckCircle2 size={34} /></div>
                  <h3 className="font-display text-2xl font-bold text-slate-900">Thank you!</h3>
                  <p className="mt-2 text-slate-500">Your message has been sent. Our team will reach out shortly.</p>
                  <button onClick={() => setDone(false)} className="mt-6 rounded-full glass px-6 py-3 text-sm font-semibold text-slate-900 hover:border-[#2563eb]/50 transition-colors">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glass-strong rounded-3xl p-7 md:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label htmlFor="name" className="text-slate-600">Name *</Label><Input id="name" value={form.name} onChange={set('name')} placeholder="Jane Doe" className={inputCls} /></div>
                  <div className="space-y-2"><Label htmlFor="email" className="text-slate-600">Work Email *</Label><Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="you@company.com" className={inputCls} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label htmlFor="company" className="text-slate-600">Company</Label><Input id="company" value={form.company} onChange={set('company')} placeholder="Company name" className={inputCls} /></div>
                  <div className="space-y-2"><Label htmlFor="website" className="text-slate-600">Website / App</Label><Input id="website" value={form.website} onChange={set('website')} placeholder="https://" className={inputCls} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Industry</Label>
                    <Select value={form.industry} onValueChange={set('industry')}>
                      <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 h-12"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{contactOptions.industries.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Monthly Marketing Budget</Label>
                    <Select value={form.budget} onValueChange={set('budget')}>
                      <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 h-12"><SelectValue placeholder="Select budget" /></SelectTrigger>
                      <SelectContent>{contactOptions.budgets.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label htmlFor="markets" className="text-slate-600">Target Markets</Label><Input id="markets" value={form.markets} onChange={set('markets')} placeholder="e.g. US, UK, India" className={inputCls} /></div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Campaign Type</Label>
                    <Select value={form.campaign_type} onValueChange={set('campaign_type')}>
                      <SelectTrigger className="bg-slate-100 border-slate-200 text-slate-900 h-12"><SelectValue placeholder="Select campaign type" /></SelectTrigger>
                      <SelectContent>{contactOptions.campaignTypes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label htmlFor="message" className="text-slate-600">Message *</Label><Textarea id="message" value={form.message} onChange={set('message')} placeholder="Tell us about your goals..." rows={5} className="bg-slate-100 border-slate-200 text-slate-900 placeholder:text-slate-500 resize-none" /></div>
                <button type="submit" disabled={submitting} className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] px-7 py-4 font-semibold text-white hover:opacity-90 transition-opacity blue-glow disabled:opacity-60">
                  {submitting ? 'Sending...' : 'Start the Conversation'}
                  {!submitting && <Send size={18} className="group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
